import React, { useState } from 'react';
import {
  ILegalClientFullObject,
  type ILegalClient,
} from '../../Interfaces/ILegalClient';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, deDE, type GridColDef } from '@mui/x-data-grid';
import { Check, Close } from '@mui/icons-material';
import type { IArnoTop } from '../../Interfaces/IArnoTop';
import { useGetArnoTopData } from '../../hooks/reactQuerys/ARNOtopQueries';

const columns: GridColDef[] = [
  { field: 'idArnoTop', headerName: 'Namen-Nr.', width: 100 },
  {
    field: 'idLegalClient',
    headerName: 'Synchronisiert',
    width: 120,
    renderCell: params => (params.value !== null ? <Check /> : <Close />),
  },
  { field: 'FirstName', headerName: 'Vorname', width: 170 },
  { field: 'LastName', headerName: 'Nachname', width: 170 },
  { field: 'Email', headerName: 'E-Mail-Adresse', flex: 1 },
];

export type ArnotopSyncState = {
  idArnoTop: number | null;
  syncType: number | undefined;
  syncWithArnoTop: boolean;
};

const initialSyncState: ArnotopSyncState = {
  idArnoTop: null,
  syncType: 1,
  syncWithArnoTop: false,
};

interface IProps {
  legalClient: ILegalClientFullObject | ILegalClient;
  onSave: (arnotopSyncState: ArnotopSyncState) => void;
}

const getMatchScore = (
  arnoTopEntry: IArnoTop,
  legalClient: ILegalClientFullObject | ILegalClient
) => {
  let score = 0;
  if (
    arnoTopEntry.FirstName &&
    legalClient.FirstName &&
    arnoTopEntry.FirstName.toLowerCase() === legalClient.FirstName.toLowerCase()
  )
    score++;
  if (
    arnoTopEntry.LastName &&
    legalClient.LastName &&
    arnoTopEntry.LastName.toLowerCase() === legalClient.LastName.toLowerCase()
  )
    score++;
  if (
    arnoTopEntry.Email &&
    legalClient.Email &&
    arnoTopEntry.Email.toLowerCase() === legalClient.Email.toLowerCase()
  )
    score++;
  return score;
};

export const AddArnotopSyncDialog: React.FC<IProps> = ({
  legalClient,
  onSave,
}) => {
  const { arnoTopData, isLoadingArnoTopData, refetchArnoTopData } =
    useGetArnoTopData();

  const [arnotopSyncState, setArnotopSyncState] =
    useState<ArnotopSyncState>(initialSyncState);

  const entriesWithMatchScore =
    arnoTopData?.map(entry => ({
      ...entry,
      matchScore: getMatchScore(entry, legalClient),
    })) ?? [];

  const sortedEntries = entriesWithMatchScore.sort(
    (a, b) => b.matchScore - a.matchScore
  );

  const handleCreateNew = () => {
    const updatedArnotopSyncState: ArnotopSyncState = {
      ...arnotopSyncState,
      idArnoTop: null,
      syncWithArnoTop: true,
    };

    onSave(updatedArnotopSyncState);
  };

  const handleAddSync = () => {
    if (!arnotopSyncState.idArnoTop) {
      return;
    }

    const updatedArnotopSyncState: ArnotopSyncState = {
      ...arnotopSyncState,
      syncWithArnoTop: true,
    };

    onSave(updatedArnotopSyncState);
  };

  const handleAbort = () => {
    const updatedArnotopSyncState: ArnotopSyncState = {
      ...arnotopSyncState,
      idArnoTop: null,
      syncWithArnoTop: false,
      syncType: undefined,
    };

    onSave(updatedArnotopSyncState);
  };

  if (isLoadingArnoTopData) {
    return (
      <Dialog open onClose={handleAbort} maxWidth='md' fullWidth scroll='paper'>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
          <Typography variant='body1'>
            Daten werden aus ARNOtop geladen. Das kann einige Sekunden dauern.
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  if (!arnoTopData) {
    return (
      <Dialog open onClose={handleAbort} maxWidth='md' fullWidth scroll='paper'>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            minHeight: '50vh',
          }}
        >
          <Typography variant='body1'>
            Fehler beim Laden der Daten. Bitte versuchen Sie es erneut.
          </Typography>
          <Button variant='contained' onClick={() => refetchArnoTopData()}>
            Erneut versuchen
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open onClose={handleAbort} maxWidth='md' fullWidth scroll='paper'>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 3,
            py: 2,
          }}
        >
          <Typography variant='h5'>
            Bereitgestellte Namen aus ARNOtop
          </Typography>
        </Box>

        <DialogContent>
          <Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 2,
              }}
            >
              <Typography>
                Wie soll die Synchronisation gestartet werden?
              </Typography>
              <TextField
                select
                label='Start der Synchronisation'
                size='small'
                value={arnotopSyncState.syncType}
                onChange={event =>
                  setArnotopSyncState(prev => ({
                    ...prev,
                    syncType: Number(event.target.value),
                  }))
                }
              >
                <MenuItem key={`init-sync-arno-1`} value={1}>
                  Alle Werte aus DiNo (überschreibe in ARNOtop)
                </MenuItem>
                <MenuItem key={`init-sync-arno-2`} value={2}>
                  Alle Werte aus ARNOtop (überschreibe in DiNo)
                </MenuItem>
              </TextField>
            </Box>

            <Box mt={2}>
              <DataGrid
                localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
                rows={sortedEntries ?? []}
                columns={columns}
                getRowId={row => row.idArnoTop}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 25,
                    },
                  },
                }}
                autoHeight
                onRowClick={params =>
                  setArnotopSyncState(prev => ({
                    ...prev,
                    idArnoTop: Number(params.id),
                  }))
                }
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
        >
          <Button variant='outlined' onClick={handleAbort}>
            Abbrechen
          </Button>
          <Button variant='contained' onClick={handleCreateNew}>
            Neuen Namen in ARNOtop anlegen
          </Button>
          <Button
            variant='contained'
            disabled={!arnotopSyncState.idArnoTop}
            onClick={handleAddSync}
          >
            Mit ARNOtop Namen verknüpfen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
